import { useNavigate, useSearch } from "@tanstack/react-router";
import { setupAGGrid } from "@/lib/AGGrid/setupAGGrid";
import { ReportBookingsTable } from "@/app/Reports/Reports/Components/ReportBookings/ReportBookingsTable";
import { BookingListItem } from "@/app/Bookings/GraphQL/bookingListQuery";
import { Suspense, useCallback, useRef } from "react";
import { Button } from "@/components/catalyst/button";
import { Select } from "@/components/catalyst/select";
import { DateRangePicker } from "@/components/ui/date-range/date-range-picker";
import dayjs from "dayjs";
import { AgGridReact } from "ag-grid-react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

setupAGGrid();

export function ReportBookingsScreen() {
  const gridRef = useRef<AgGridReact<BookingListItem>>(null);

  const { startDate, endDate, dateBasis } = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/bookings",
  });

  const navigate = useNavigate({
    from: "/$tenant/$orgId/supplier/{-$supplierId}/reports/bookings",
  });

  const onBtExport = useCallback(
    (type: "csv" | "xls") => () => {
      if (type === "csv") {
        gridRef.current?.api.exportDataAsCsv();
      } else {
        gridRef.current?.api.exportDataAsExcel();
      }
    },
    [],
  );

  return (
    <div className="flex h-full flex-col space-y-4 p-8">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/reports"
              params={(prev) => prev}
            >
              Reports
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Bookings</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">
            Booking Reports
          </h2>
          <p className="text-sm text-gray-500">
            View and export booking reports. The date range filters on the
            selected date basis.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Select
            aria-label="Date basis"
            className="w-48"
            value={dateBasis}
            onChange={(e) => {
              navigate({
                params: (prev) => prev,
                search: (prev) => ({
                  ...prev,
                  dateBasis: e.target.value as typeof dateBasis,
                }),
              });
            }}
          >
            <option value="DELIVER">Delivery date</option>
            <option value="DEPART">Departure date</option>
            <option value="BOOKED">Booking created</option>
            <option value="CONFIRMED">Booking confirmed</option>
            <option value="POSTED">Relocation posted</option>
          </Select>
          <DateRangePicker
            initialDateFrom={dayjs(startDate).toDate()}
            initialDateTo={dayjs(endDate).toDate()}
            onUpdate={({ range }) => {
              navigate({
                params: (prev) => prev,
                search: (prev) => ({
                  ...prev,
                  startDate: dayjs(range.from).format("YYYY-MM-DD"),
                  endDate: dayjs(range.to).format("YYYY-MM-DD"),
                }),
              });
            }}
          />
          <Button onClick={onBtExport("csv")}>Export CSV</Button>
          <Button onClick={onBtExport("xls")}>Export excel</Button>
        </div>
      </div>

      <div className="grow rounded-lg">
        <Suspense fallback={<Skeleton className="mt-3 h-80" />}>
          <ReportBookingsTable ref={gridRef} />
        </Suspense>
      </div>
    </div>
  );
}
